// AMD Settings — tenant-wide singleton (no list/CRUD).
import { useEffect, useState } from "react";
import { Loader2, Save, Bot } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import FileUpload from "@/admin/components/FileUpload";

const PRESET_MAX_MS = { fast: 2500, balanced: 5000, accurate: 7000 };
const maxDelayMs = (cfg) =>
  PRESET_MAX_MS[cfg.sensitivity_preset || "balanced"] ?? Number(cfg.max_analysis_ms || 0);

export default function AMDPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { canWrite } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/amd-settings"); setCfg(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try { await api.put("/admin/amd-settings", cfg); toast.success("AMD settings saved"); load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  if (loading || !cfg) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="amd-title">AMD — Answering Machine Detection</h1>
          <p className="text-sm text-muted-foreground mt-1">Tenant-wide default. Campaigns can opt-in or out via a single toggle.</p>
        </div>
        {canWrite && <Button data-testid="amd-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Save className="w-4 h-4"/> Save</Button>}
      </div>
      <Card className="p-5 space-y-5">
        <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-primary"/><h2 className="font-display font-bold">Detection</h2></div>
        <div className="flex items-center gap-2"><Switch data-testid="amd-enabled" checked={!!cfg.enabled} onCheckedChange={(v)=>setCfg({...cfg, enabled:!!v})} /><Label>Enabled (default for new campaigns)</Label></div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Sensitivity preset</Label>
            <Select value={cfg.sensitivity_preset || "balanced"} onValueChange={(v)=>setCfg({...cfg, sensitivity_preset:v})}>
              <SelectTrigger data-testid="amd-preset" className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="fast">Fast (≤ 2.5 s connect delay — recommended)</SelectItem>
                <SelectItem value="balanced">Balanced (≤ 5 s connect delay)</SelectItem>
                <SelectItem value="accurate">Accurate (≤ 7 s, fewest false hits)</SelectItem>
                <SelectItem value="custom">Custom (advanced params)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="amd-delay-hint">
              AMD listens before the call reaches the agent (or the broadcast plays). Worst-case added delay: <span className="font-mono font-semibold">{(maxDelayMs(cfg) / 1000).toFixed(1)} s</span>.
            </p>
          </div>
          <div><Label>Ambiguous result (NOTSURE) counts as</Label>
            <Select value={cfg.notsure_as || "human"} onValueChange={(v)=>setCfg({...cfg, notsure_as:v})}>
              <SelectTrigger data-testid="amd-notsure" className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="human">Human (connect to agent — safest)</SelectItem>
                <SelectItem value="machine">Machine (apply machine action)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {cfg.sensitivity_preset === "custom" && (
          <div className="border rounded-md p-4 bg-muted/20 space-y-3" data-testid="amd-advanced">
            <p className="text-xs text-muted-foreground">Advanced Asterisk <span className="font-mono">AMD()</span> parameters (all in milliseconds unless noted).</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Initial silence</Label><Input type="number" data-testid="amd-adv-initial" value={cfg.initial_silence_ms} onChange={(e)=>setCfg({...cfg, initial_silence_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Greeting</Label><Input type="number" data-testid="amd-adv-greeting" value={cfg.greeting_ms} onChange={(e)=>setCfg({...cfg, greeting_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">After-greeting silence</Label><Input type="number" data-testid="amd-adv-aftergreet" value={cfg.after_greeting_silence_ms} onChange={(e)=>setCfg({...cfg, after_greeting_silence_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Total analysis</Label><Input type="number" data-testid="amd-adv-total" value={cfg.max_analysis_ms} onChange={(e)=>setCfg({...cfg, max_analysis_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Min word length</Label><Input type="number" data-testid="amd-adv-minword" value={cfg.min_word_ms} onChange={(e)=>setCfg({...cfg, min_word_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Between-words silence</Label><Input type="number" data-testid="amd-adv-between" value={cfg.between_words_silence_ms} onChange={(e)=>setCfg({...cfg, between_words_silence_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Max words</Label><Input type="number" data-testid="amd-adv-maxwords" value={cfg.max_words} onChange={(e)=>setCfg({...cfg, max_words:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Silence threshold</Label><Input type="number" data-testid="amd-adv-silence" value={cfg.silence_threshold_ms} onChange={(e)=>setCfg({...cfg, silence_threshold_ms:Number(e.target.value)})} /></div>
              <div><Label className="text-xs">Max word length</Label><Input type="number" data-testid="amd-adv-maxword" value={cfg.max_word_ms} onChange={(e)=>setCfg({...cfg, max_word_ms:Number(e.target.value)})} /></div>
            </div>
          </div>
        )}
        <div className="border-t pt-4">
          <h2 className="font-display font-bold mb-3">On Answering Machine</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Action</Label>
              <Select value={cfg.on_machine} onValueChange={(v)=>setCfg({...cfg, on_machine:v})}>
                <SelectTrigger data-testid="amd-action" className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="drop">Drop call (hang up, mark AMD)</SelectItem>
                  <SelectItem value="play_voicemail">Leave voicemail message</SelectItem>
                  <SelectItem value="transfer_to_agent">Transfer to agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {cfg.on_machine === "play_voicemail" && (
            <div className="mt-3">
              <Label>Voicemail audio</Label>
              <FileUpload kind="audio_prompts" value={cfg.voicemail_audio_url||""} onChange={(url)=>setCfg({...cfg, voicemail_audio_url:url})} accept="audio/*" testId="amd-vm-audio" />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">Applies to all auto-dial modes (predictive, power, progressive, auto). Voice-broadcast campaigns can override this per campaign.</p>
        </div>
      </Card>
    </div>
  );
}
